import axios from 'axios';
import i18n from '@/config/i18n';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import { useUsersStore } from '@/store/users-store';

axios.interceptors.request.use(
  function (config) {
    config.headers['Accept-Language'] = i18n.global.locale.value;
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

function sentWithCurrentToken(response) {
  return (
    response?.config?.headers?.Authorization ===
    `Bearer ${useUsersStore().user.token}`
  );
}

function captureRefreshedToken(response) {
  if (response?.data?.token !== undefined && sentWithCurrentToken(response)) {
    useUsersStore().refreshToken(response.data.token);
  }
}

axios.interceptors.response.use(
  function (response) {
    captureRefreshedToken(response);
    return response;
  },
  function (error) {
    captureRefreshedToken(error.response);
    return Promise.reject(error);
  },
);

export default {
  async post(url, data, queryParams = {}, options = {}) {
    try {
      const anonymous = options.anonymous === true;
      return await axios({
        method: 'post',
        url,
        data,
        params: pickBy(queryParams, identity),
        headers: anonymous ? {} : getAuthHeader(),
        anonymous,
      });
    } catch (exception) {
      return handleApiError(exception);
    }
  },

  async put(url, data) {
    try {
      const headers = getAuthHeader();
      headers['X-HTTP-Method-Override'] = 'PUT';
      return await axios({
        method: 'post',
        url,
        data,
        headers,
      });
    } catch (exception) {
      return handleApiError(exception);
    }
  },

  async get(url, queryParams = {}) {
    try {
      return await axios({
        method: 'get',
        url,
        params: pickBy(queryParams, identity),
        headers: getAuthHeader(),
      });
    } catch (exception) {
      return handleApiError(exception);
    }
  },

  async delete(url, data = {}) {
    try {
      return await axios({
        method: 'delete',
        url,
        data,
        headers: getAuthHeader(),
      });
    } catch (exception) {
      return handleApiError(exception);
    }
  },

  downloadFile(url, data = {}) {
    return axios({
      method: 'post',
      responseType: 'blob',
      url,
      data,
      headers: {
        ...getAuthHeader(),
      },
    });
  },
};

function handleApiError(exception) {
  if (exception.response !== undefined) {
    const usersStore = useUsersStore();
    const loggedIn = usersStore.isLoggedIn;
    const unauthorized = exception.response.status === 401;
    if (unauthorized && loggedIn && exception.config?.anonymous !== true) {
      usersStore.parkSession();
    } else if (unauthorized && !loggedIn) {
      exception.response.data.data.message = '';
    }
    return Promise.reject({
      ...exception.response.data.data,
      status: exception.response.status,
    });
  } else {
    return Promise.reject(exception);
  }
}

export function getAuthHeader() {
  // return authorization header with jwt token
  const token = useUsersStore().user.token;
  if (token) {
    return { Authorization: 'Bearer ' + token };
  } else {
    return {};
  }
}

/**
 * A appeler avant un POST lorsqu'on envoie une image avec du contenu
 * @param dto
 */

export function buildFormData(dto, parentKey = '', formData = new FormData()) {
  for (const property in dto) {
    const key = parentKey ? `${parentKey}[${property}]` : property;
    const value = dto[property];

    if (Array.isArray(value)) {
      if (value.length > 0 && value[0] instanceof File) {
        value.forEach((file) => {
          formData.append(key + '[]', file);
        });
      } else {
        value.forEach((item, index) => {
          if (
            typeof item === 'object' &&
            item !== null &&
            !(item instanceof File)
          ) {
            buildFormData(item, `${key}[${index}]`, formData);
          } else {
            formData.append(
              `${key}[${index}]`,
              item === null ? '' : String(item),
            );
          }
        });
      }
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'object' && value !== null) {
      buildFormData(value, key, formData);
    } else if (typeof value === 'boolean') {
      formData.append(key, value ? '1' : '0');
    } else if (value !== null) {
      formData.append(key, String(value));
    }
  }

  return formData;
}
